import type { UiMessageModule } from "./merge";

export const dashboardPageMessages = {
	en: {
		account: {
			accountDeleted: "Account Deleted",
			accountDeletedDescription: "Your account has been successfully deleted.",
			accountInformation: "Account Information",
			areYouSure: "Are you absolutely sure?",
			cancel: "Cancel",
			deleteAccount: "Delete Account",
			deleteAccountConfirmDescription:
				"This permanently deletes your account and personal data, including login credentials and personal API keys. This action cannot be undone. Billing records of credits you purchased and spent are retained for 10 years as required by tax and accounting law.",
			deleteAccountDescription:
				"Permanently delete your account and personal data",
			deleteAccountWarning:
				"This action is irreversible. Your account and personal data, including login credentials and personal API keys, will be permanently deleted.",
			deleting: "Deleting...",
			email: "Email",
			emailCannotChange: "email cannot be changed",
			emailPassword: "Email & Password",
			error: "Error",
			genericError: "An error occurred",
			name: "Name",
			privacyPolicy: "Privacy Policy",
			passkey: "Passkey",
			retentionNoticeBeforeLink:
				"Billing records of credits you purchased and spent are retained for 10 years as required by tax and accounting law. See our",
			retentionNoticeAfterLink: "for details.",
			retentionNotice:
				"Billing records of credits you purchased and spent are retained for 10 years as required by tax and accounting law. See our {link} for details.",
			saveChanges: "Save Changes",
			saving: "Saving...",
			signedInVia: "Signed in via",
			success: "Success",
			title: "Account",
			updateDescription: "Update your account details",
			updatedDescription: "Your account information has been updated.",
		},
		billing: {
			autoTopUpEnabled: "Auto top-up enabled",
			autoTopUpEnabledDescription:
				"Credits will automatically reload ${amount} when your balance drops below ${threshold}.",
			autoTopUpNudgeDescription:
				"Auto-reload ${amount} when balance drops below ${threshold}",
			autoTopUpNudgeTitle: "Never run out of credits",
			dismiss: "Dismiss",
			paymentCanceled: "Payment canceled",
			paymentCanceledDescription: "Your payment was canceled.",
			paymentSuccessful: "Payment successful",
			paymentSuccessfulDescription:
				"Your payment has been processed successfully.",
		},
		dashboard: {
			errorLoadingProjects: "Error Loading Projects",
			errorLoadingProjectsDescription:
				"Failed to load projects for this organization. Please check your connection and try again.",
			noProjectsFound: "No Projects Found",
			noProjectsFoundDescription:
				"This organization doesn't have any projects yet. Create a project to get started.",
		},
		apiKeys: {
			activate: "Activate",
			addRuleDescription:
				"Add access control rules to restrict model access by type, provider, or pricing.",
			allowIpRanges: "Allow IP Ranges (CIDR)",
			allowPricingConstraints: "Allow Pricing Constraints",
			allowSpecificModels: "Allow Specific Models",
			allowSpecificProviders: "Allow Specific Providers",
			allModelsAccessible:
				"All models are accessible. Create a rule above to restrict access.",
			allTimeUsageLimit: "All-time usage limit",
			any: "Any",
			backToApiKeys: "Back to API Keys",
			createdOn: "Created {date}",
			createNewRule: "Create New Rule",
			createRule: "Create Rule",
			currentPeriodResetDescription:
				"Current period usage resets when the configured window elapses.",
			deactivate: "Deactivate",
			deleteRule: "Delete rule",
			denyIpRanges: "Deny IP Ranges (CIDR)",
			denyPricingConstraints: "Deny Pricing Constraints",
			denySpecificModels: "Deny Specific Models",
			denySpecificProviders: "Deny Specific Providers",
			durationRange: "Duration must be between 1 and {max} {unit}.",
			editLimits: "Edit API key limits",
			editLimitsDescription:
				"Update the all-time limit and the recurring usage window for this key.",
			enterAllTimeLimit: "Enter an all-time usage limit or turn it off.",
			enterRecurringLimit: "Enter a recurring usage limit or turn it off.",
			every: "Every",
			expirationDescription:
				"The key is automatically disabled once it expires. You can reactivate it later by setting a new expiration.",
			expirationRange: "Expiration must be between 1 and {max} {unit}.",
			existingRules: "Existing Rules",
			expiresIn: "Expires in",
			expiresOn: "Expires on {date}.",
			freeOnly: "Free Only",
			groupModelsPlaceholder: "Select models...",
			groupModelsSelected: "{count} models selected",
			groupProvidersPlaceholder: "Select providers...",
			groupProvidersSelected: "{count} providers selected",
			iamDescription: "Configure access control rules for {keyName}",
			ipRangesHelp:
				"Comma or whitespace separated. IPv4 and IPv6 supported. The gateway reads the client IP from the first entry in {header} (set by the GCP load balancer).",
			invalidCidr: "Invalid CIDR",
			invalidCidrDescription: "Not a valid CIDR: {cidr}",
			lastUsed: "Last Used",
			limitUsageIncludes:
				"Usage includes both usage from doteb credits and usage from your own provider keys when applicable.",
			loadingRules: "Loading rules...",
			manageRulesDescription:
				"Manage the access control rules for this API key.",
			maxInputPrice: "Max Input Price ($/M tokens)",
			maxOutputPrice: "Max Output Price ($/M tokens)",
			models: "Models",
			noConstraints: "No constraints",
			noModelsFound: "No models found.",
			noProvidersFound: "No providers found.",
			paidOnly: "Paid Only",
			pricingType: "Pricing Type",
			recurringLimitNonNegative:
				"Recurring usage limit must be a non-negative number.",
			recurringUsageLimit: "Recurring usage limit",
			restrictions: "Restrictions",
			ruleCreated: "IAM rule created successfully",
			ruleDeleted: "IAM rule deleted successfully",
			ruleType: "Rule Type",
			saveChanges: "Save changes",
			searchModels: "Search models...",
			searchProviders: "Search providers...",
			selectPricingType: "Select pricing type",
			setAllTimeUsageLimit: "Set all-time usage limit",
			setExpirationTtl: "Set expiration (TTL)",
			setRecurringUsageLimit: "Set recurring usage limit",
			stabilityBeta: "Beta",
			stabilityExperimental: "Experimental",
			stabilityUnstable: "Unstable",
			typeLabel: "Type: {type}",
			unit: "Unit",
			usageLimitNonNegative:
				"All-time usage limit must be a non-negative number.",
			windowEvery: "Every {window}",
			windowReset: "Resets {date}",
			yourApiKeys: "Your API Keys",
		},
		auditLogs: {
			accessDenied: "Access Denied",
			accessDeniedDescription:
				"Only organization owners and admins can view audit logs.",
			action: "Action:",
			allActions: "All actions",
			allResources: "All resources",
			auditLogs: "Audit Logs",
			copyResourceId: "Copy resource ID to clipboard",
			copyToClipboard: "Copy to clipboard",
			copied: "Copied",
			description:
				"View a complete history of all actions taken within your organization.",
			details: "Details",
			failedLoad: "Failed to load audit logs",
			loadMore: "Load More",
			loading: "Loading...",
			noLogs: "No audit logs found",
			resource: "Resource:",
			resourceColumn: "Resource",
			resourceId: "Resource ID",
			timestamp: "Timestamp",
			user: "User",
		},
		activity: {
			activityLogs: "Activity Logs",
			agent: "agent",
			agents: "agents",
			agentsSummary: "{count} {label}",
			allIntegrations: "All integrations",
			allModels: "All models",
			allProviders: "All providers",
			allUnifiedReasons: "All unified reasons",
			backToAgents: "Back to agents",
			cost: "Cost",
			cursor: "Cursor",
			customHeaderKeyPlaceholder: "Custom header key (e.g., uid)",
			customHeaderValuePlaceholder: "Custom header value (e.g., 12345)",
			duration: "Duration",
			errorLoadingAgents: "Failed to load agent data. Please try again.",
			errorLoadingLogs: "Error loading logs",
			errorLoadingSessions: "Failed to load sessions. Please try again.",
			filterByModel: "Filter by model",
			filterByProvider: "Filter by provider",
			filterBySource: "Filter by source",
			filterByUnifiedReason: "Filter by unified reason",
			firstCallWorked: "Your first API call worked!",
			firstCallWorkedDescription:
				"Top up credits to unlock paid models with higher quality, faster speeds, and larger context windows.",
			finishReasons: {
				canceled: "Canceled",
				completed: "Completed",
				content_filter: "Content Filter",
				gateway_error: "Gateway Error",
				length_limit: "Length Limit",
				tool_calls: "Tool Calls",
				unknown: "Unknown",
				upstream_error: "Upstream Error",
			},
			daysAgo: "{count}d ago",
			hoursAgo: "{count}h ago",
			lastActive: "Last active",
			loadMore: "Load More",
			loading: "Loading...",
			loadingAgents: "Loading agents...",
			loadingMore: "Loading more...",
			loadingSessions: "Loading sessions...",
			loadMoreSessions: "Load more sessions",
			noAgentActivity: "No agent activity yet",
			noAgentActivityDescription:
				"Activity appears when coding agents like Claude Code, Autohand Code, OpenCode, Cursor, or Cline make API requests through the gateway.",
			noLogs: "No logs found matching the selected filters.",
			noModels: "No models found.",
			noProject: "Please select a project to view recent logs.",
			noSessions: "No sessions yet",
			noSessionsDescription:
				"Sessions appear when coding tools like Claude Code, Autohand Code, OpenCode, SoulForge, or Cursor make API requests through the gateway.",
			noSessionsForAgent: "No sessions found for this agent.",
			project: "Project: {projectId}",
			recentEventsDescription: "Your recent API requests and system events",
			refresh: "Refresh",
			refreshing: "Refreshing...",
			recently: "Recently",
			request: "request",
			requests: "requests",
			requestsTitle: "Requests",
			searchModels: "Search models...",
			sessionIdPlaceholder: "Session ID",
			topUpCredits: "Top Up Credits",
			totalTokens: "Total tokens",
			tokens: "tokens",
			tokensTitle: "Tokens",
		},
		common: {
			actions: "Actions",
			cancel: "Cancel",
			category: "Category",
			created: "Created",
			delete: "Delete",
			disabled: "Disabled",
			done: "Done",
			enabled: "Enabled",
			error: "Error",
			loading: "Loading...",
			reset: "Reset",
			save: "Save",
			saveChanges: "Save Changes",
			saveSettings: "Save Settings",
			saving: "Saving...",
			settingsSaved: "Settings saved",
			success: "Success",
			unknown: "Unknown",
		},
		dateRange: {
			last1Hour: "Last 1 hour",
			last1Minute: "Last 1 minute",
			last2Hours: "Last 2 hours",
			last3Days: "Last 3 days",
			last4Hours: "Last 4 hours",
			last5Minutes: "Last 5 minutes",
			last7Days: "Last 7 days",
			last12Hours: "Last 12 hours",
			last14Days: "Last 14 days",
			last24Hours: "Last 24 hours",
			last30Days: "Last 30 days",
			last30Minutes: "Last 30 minutes",
			searchPlaceholder: "Search time ranges",
			selectTimeRange: "Select time range",
			today: "Today",
		},
		guardrails: {
			accessDenied: "Access Denied",
			accessDeniedDescription:
				"Only organization owners and admins can manage guardrails.",
			action: "Action",
			actionLabel: "Action: {action}",
			addFileTypePlaceholder: "Add file type (e.g., pdf)",
			addRule: "Add Rule",
			allow: "Allow",
			allowLogOnly: "Allow (Log Only)",
			allowedFileTypes: "Allowed File Types",
			block: "Block",
			blockedTerms: "Blocked Terms",
			blockedTermsLabel: "Blocked Terms (one per line)",
			configSaved: "Configuration saved successfully",
			customRegex: "Custom Regex",
			customRules: "Custom Rules",
			customRulesDescription: "Create custom content filtering rules",
			deleteRuleFailed: "Failed to delete rule",
			disabled: "Disabled",
			documentLeakageDescription:
				"Prevent exposure of confidential document content",
			documentLeakageName: "Document Leakage Prevention",
			enabled: "Enabled",
			failedAddRule: "Failed to add rule",
			failedLoad: "Failed to load guardrails configuration",
			failedSave: "Failed to save configuration",
			failedUpdateRule: "Failed to update rule",
			fileRestrictions: "File Restrictions",
			fileRestrictionsDescription:
				"Configure allowed file types and size limits",
			fileTypesDescription: "Restrict uploads to allowed file types",
			fileTypesName: "File Type Restrictions",
			guardrails: "Guardrails",
			guardrailsDescription:
				"Configure content safety rules for your LLM applications",
			jailbreakDescription: "Block attempts to bypass AI safety measures",
			jailbreakName: "Jailbreak Prevention",
			maxFileSize: "Maximum File Size (MB)",
			newRule: "New Rule",
			noCustomRules: "No custom rules configured.",
			noCustomRulesAdd: ' Click "Add Rule" to create one.',
			noCustomRulesEnable: " Enable guardrails to add rules.",
			piiDescription:
				"Detect and optionally redact personal identifiable information",
			piiName: "PII Detection",
			promptInjectionDescription:
				"Detect attempts to override system instructions or inject malicious prompts",
			promptInjectionName: "Prompt Injection Detection",
			redact: "Redact",
			regexPattern: "Regex Pattern",
			restrictedTopics: "Restricted Topics (one per line)",
			ruleName: "Rule Name",
			ruleNamePlaceholder: "e.g., Block competitors",
			ruleType: "Rule Type",
			saveChanges: "Save Changes",
			saving: "Saving...",
			secretsDescription: "Detect API keys, passwords, and other credentials",
			secretsName: "Secrets Detection",
			systemRules: "System Rules",
			systemRulesDescription:
				"Built-in security rules powered by pattern matching",
			topicRestriction: "Topic Restriction",
			warn: "Warn",
		},
		logDetail: {
			additionalParameters: "Additional Parameters",
			apiKey: "API Key",
			applied: "Applied",
			aspectRatio: "Aspect Ratio",
			audioInputCost: "Audio Input Cost",
			audioInputTokens: "Audio Input Tokens",
			available: "Available",
			availableTools: "Available Tools",
			backToActivity: "Back to Activity",
			billedToOrganization: "Billed to your organization",
			builtinTools: "Builtin Tools",
			cache: "Cache",
			cachedInputCost: "Cached Input Cost",
			cachedInputTokens: "Cached Input Tokens",
			cacheWriteCost: "Cache Write Cost",
			cacheWriteTokens: "Cache Write Tokens",
			canceled: "Canceled",
			completionTokens: "Completion Tokens",
			compression: "Compression",
			contentFilter: "Content Filter",
			contentFilterCost: "Content Filter Cost",
			completed: "Completed",
			copyApiKeyId: "Copy API key ID",
			copyLogId: "Copy log ID",
			copyProjectId: "Copy project ID",
			copyRequestId: "Copy request ID",
			copyTraceId: "Copy trace ID",
			cost: "Cost: ${cost}",
			costInformation: "Cost Information",
			customHeaders: "Custom Headers",
			dataStorage: "Data Storage",
			date: "Date",
			discount: "Discount",
			discountOff: "{percent}% off",
			duration: "Duration",
			error: "Error",
			errorDetails: "Error Details",
			errorMessage: "Error Message",
			effort: "Effort",
			finishReason: "Finish Reason",
			frequencyPenalty: "Frequency Penalty",
			full: "Full",
			generatedImageAlt: "Generated image {index}",
			imageCount: "Image Count",
			imageGeneration: "Image Generation",
			imageInputCost: "Image Input Cost",
			imageInputTokens: "Image Input Tokens",
			imageOutputCost: "Image Output Cost",
			imageOutputTokens: "Image Output Tokens",
			imageQuality: "Image Quality",
			imageSize: "Image Size",
			idPrefix: "ID: {id}",
			inferenceCost: "Inference Cost",
			inferenceTotal: "Inference Total",
			inputCost: "Input Cost",
			inputImageAlt: "Input image {index}",
			key: "Key",
			logId: "Log ID",
			logNotFound: "Log not found",
			maxTokens: "Max Tokens",
			messageDataNotRetained:
				"Message data not retained. Enable retention in organization policies to store request messages.",
			messages: "Messages",
			metadata: "Metadata",
			modelMapping: "Model Mapping",
			mode: "Mode",
			no: "No",
			noMessageData: "No message data available.",
			none: "None",
			noResponseContent: "No response content available.",
			notNeeded: "Not needed",
			ok: "ok",
			outputCost: "Output Cost",
			outputFormat: "Output Format",
			parameters: "Parameters",
			partial: "Partial",
			plugins: "Plugins",
			pricingTier: "Pricing Tier",
			project: "Project",
			promptTokens: "Prompt Tokens",
			provider: "Provider",
			providerCost: "Provider cost",
			providerCostNoBalance: "Provider cost - not deducted from your balance",
			providerPricing: "Provider pricing",
			providerScores: "Provider Scores",
			rpmCapped: "rpm capped",
			reasoningContent: "Reasoning Content",
			reasoningEffort: "Reasoning Effort",
			reasoningTokens: "Reasoning Tokens",
			request: "Request",
			requestAttempts: "Request Attempts",
			requestCost: "Request Cost",
			requestedModel: "Requested Model",
			requestedProvider: "Requested Provider",
			requestId: "Request ID",
			response: "Response",
			responseContentNotRetained:
				"Response content not retained. Enable retention in organization policies to store response data.",
			responseFormat: "Response Format",
			responseHealing: "Response Healing:",
			responseSize: "Response Size",
			retried: "Retried",
			retrySucceeded: "This request was retried and succeeded.",
			routing: "Routing",
			selection: "Selection",
			serviceTier: "Service Tier",
			seed: "Seed",
			source: "Source",
			statusCode: "Status Code",
			statusText: "Status Text",
			streamed: "Streamed",
			throughput: "Throughput",
			tokens: "Tokens",
			temperature: "Temperature",
			toolCalls: "Tool Calls",
			toolChoice: "Tool Choice",
			tools: "Tools",
			totalTokens: "Total Tokens",
			topP: "Top P",
			traceId: "Trace ID",
			ttft: "TTFT",
			unifiedFinishReason: "Unified Finish Reason",
			unknown: "Unknown",
			up: "up",
			usedMode: "Used Mode",
			usedModel: "Used Model",
			videoOutputCost: "Video Output Cost",
			viewSuccessfulRequest: "View successful request",
			webSearchCost: "Web Search Cost",
			webSearchUsed: "Web search was used in this request",
			yes: "Yes",
		},
		masterKeys: {
			activateKey: "Activate Key",
			actions: "Actions",
			createDescription:
				"Master keys let you create projects and gateway API keys programmatically via the /v1/master/* API.",
			createMasterKey: "Create Master Key",
			created: "Created",
			createdBy: "Created By",
			deactivateKey: "Deactivate Key",
			deleteConfirmDescription:
				"This action cannot be undone. Any system using this master key will immediately lose access to the /v1/master/* API.",
			deleteConfirmShort: "This action cannot be undone.",
			deleteConfirmTitle: "Delete this master key?",
			deleted: "Master key deleted.",
			description:
				"Manage master keys to create projects and gateway API keys programmatically via the /v1/master/* API.",
			done: "Done",
			failedCreate: "Failed to create master key.",
			failedLoad: "Failed to load master keys. Please try again.",
			keyCopied: "Master Key Copied",
			keyCopiedDescription: "The master key has been copied to your clipboard.",
			keyLimitReached: "Master key limit reached",
			lastUsed: "Last Used",
			limitMax: "Maximum {max} master keys per organization",
			limitReachedContact:
				"Limit reached - contact us at contact@doteb.com to unlock more",
			loading: "Loading master keys...",
			masterKey: "Master Key",
			masterKeysUsed: "Master Keys: {current} of {max} used",
			name: "Name",
			nameRequired: "Please enter a master key name.",
			never: "Never",
			noKeys: "No master keys have been created yet.",
			openMenu: "Open menu",
			placeholderName: "e.g. Tenant Provisioning",
			secureStore:
				"Store this master key securely. It will not be displayed again.",
			status: "Status",
			statusUpdated: "Master key status updated.",
			title: "Master Keys",
			viewDocs: "View docs",
		},
		modeToggle: {
			dark: "Dark",
			light: "Light",
			system: "System",
			toggleTheme: "Toggle theme",
		},
		providerKeys: {
			addKeyDescription: "Add {count} key",
			addKeysDescription: "Add {count} keys",
			apiKeysRegionSpecific:
				"API keys are region-specific. Make sure your key matches the selected region.",
			apiVersion: "API Version",
			azureApiVersionHint: "Azure API version (default: 2024-10-21 GA)",
			azureDeploymentHint:
				"Choose Azure AI Foundry (unified endpoint) or Azure OpenAI (deployment-based)",
			customNameHint: "Used in model names like: {name}/gpt-4o",
			deploymentType: "Deployment Type",
			googleProjectHint:
				"Your Google Cloud project ID, found in the Google Cloud Console. Required for non-lite Vertex AI models.",
			googleProjectId: "Google Cloud Project ID",
			lastUpdated: "Last Updated",
			organizationLine: "Organization: {name}",
			region: "Region",
			resourceName: "Resource Name",
			resourceNameHint:
				"Your Azure resource name from the base URL: https://<resource-name>.openai.azure.com",
			selectDeploymentType: "Select deployment type",
			selectRegion: "Select region",
			validationModel: "Validation Model",
			validationModelHint:
				"Model deployment name to use for validating the API key (default: gpt-4o-mini)",
			yourProviderKeys: "Your Provider Keys",
		},
		routing: {
			availableAllPlans: "Available on all plans.",
			cacheHelp: "Bonus for providers with prompt caching",
			cacheLabel: "Cache",
			cachePromptTokensHelp: "Minimum prompt size to factor in prompt caching",
			cachePromptTokensLabel: "Cache Prompt Tokens",
			contactSales: "Contact Sales",
			defaultLatencyHelp: "Assumed latency when no metrics are available",
			defaultLatencyLabel: "Default Latency (ms)",
			defaultPlaceholder: "Default: {value}",
			defaultThroughputHelp: "Assumed throughput when no metrics are available",
			defaultThroughputLabel: "Default Throughput (tok/s)",
			defaultUptimeHelp: "Assumed uptime when no metrics are available",
			defaultUptimeLabel: "Default Uptime (%)",
			description:
				"Choose how the gateway selects a provider for this project.",
			disabled: "Disabled",
			enterprise: "Enterprise",
			enterpriseDescription:
				"Routing overrides are available on the Enterprise plan",
			enterpriseFeature: "Enterprise Feature",
			enabledDescription:
				"When disabled, this project uses the default routing values.",
			explorationRateHelp: "Fraction of requests routed to a random provider",
			explorationRateLabel: "Exploration Rate",
			failedLoad: "Failed to load routing configuration",
			failedReset: "Failed to reset routing configuration",
			failedSave: "Failed to save routing configuration",
			gatewayTimeoutHelp:
				"Max end-to-end request time. Must be <= the infra default.",
			gatewayTimeoutLabel: "Gateway Timeout (ms)",
			historyDescription:
				"How many minutes of provider metrics are considered when scoring, and how recent minutes are weighted relative to older ones.",
			historyTitle: "Metrics History Window",
			imagePriceHelp: "Weight for image-generation models",
			imagePriceLabel: "Image Price",
			latencyHelp: "Weight for streaming response time",
			latencyLabel: "Latency",
			lowUptimeFallbackHelp:
				"If requested provider is below this, reroute automatically",
			lowUptimeFallbackLabel: "Low Uptime Fallback (%)",
			maxRetriesHelp: "Maximum cross-provider fallback attempts",
			maxRetriesLabel: "Max Retries",
			mustBeAtLeast: "Must be >= {min}.",
			mustBeAtMost: "Must be <= {max} (infra ceiling).",
			plainTimeoutHelp:
				"Max upstream time for non-streaming requests. Must be <= the infra default.",
			plainTimeoutLabel: "Plain Timeout (ms)",
			priceHelp: "Weight for cost-based ranking",
			priceLabel: "Price",
			providerPrioritiesDescription:
				"Per-provider routing weight from 0 to 1. Set to 0 to exclude a provider from routing entirely.",
			providerPrioritiesTitle: "Provider Priorities",
			resetAll: "Reset all",
			resetDefaults: "Routing configuration reset to defaults",
			retryDescription: "Behavior when a provider fails or has low uptime.",
			retryTitle: "Retry & Fallback",
			routingStrategy: "Routing Strategy",
			routingStrategyDescription:
				"Set the default provider-selection strategy for this project.",
			saved: "Routing configuration saved",
			scoreMarginHelp:
				"Move away from the preferred provider only when a competitor scores at least this much better.",
			scoreMarginLabel: "Score Margin",
			scoringWeightsDescription:
				"Higher weights make a factor more influential in provider selection.",
			scoringWeightsTitle: "Scoring Weights",
			selectStrategy: "Select a strategy",
			sessionStickinessDescription:
				"Pin all requests that share a session id (the {sessionHeader} header, or the OpenAI {cacheKey}/{userField} fields) to a single provider, so multi-turn conversations keep upstream prompt caches warm. When off, every request is scored independently regardless of session id.",
			sessionStickinessTitle: "Session Stickiness",
			stickyDescription:
				"Keep routing the same provider for a model as long as it stays healthy and competitive. Reduces unnecessary switching that warms cold caches and bursts upstream rate limits.",
			stickyTitle: "Sticky Routing",
			streamingTimeoutHelp:
				"Max upstream time for streaming requests. Must be <= the infra default.",
			streamingTimeoutLabel: "Streaming Timeout (ms)",
			thresholdsDescription:
				"Defaults and cutoffs used by the scoring algorithm.",
			thresholdsTitle: "Thresholds",
			throughputHelp: "Weight for tokens-per-second efficiency",
			throughputLabel: "Throughput",
			timeoutsDescription:
				"Request and upstream timeouts in milliseconds. The infra layer enforces the shown defaults as hard ceilings - overrides may only shorten timeouts, not extend them.",
			timeoutsTitle: "Timeouts",
			title: "Routing",
			ttlSecondsHelp:
				"How long a chosen preferred provider is remembered before being re-evaluated. Capped at 24h.",
			ttlSecondsLabel: "TTL (seconds)",
			tuneDescription:
				"Tune provider selection weights, thresholds, retries, and timeouts for this project.",
			uptimeHelp: "Weight for provider availability",
			uptimeLabel: "Uptime",
			uptimePenaltyHelp: "Below this uptime, exponential penalty applies",
			uptimePenaltyLabel: "Uptime Penalty Threshold (%)",
			uptimeThresholdHelp:
				"If the preferred provider's uptime drops below this, route away from it immediately.",
			uptimeThresholdLabel: "Uptime Threshold (%)",
			windowMinutesHelp:
				"How many minutes of provider metrics to include (max 120)",
			windowMinutesLabel: "Window (minutes)",
			whatsIncluded: "What's included:",
			included1:
				"Custom scoring weights for price, uptime, throughput, latency, and prompt caching",
			included2:
				"Per-provider routing priorities (or fully exclude a provider)",
			included3: "Custom exploration rate and low-uptime fallback threshold",
			included4: "Per-project gateway and upstream request timeouts",
			included5: "Configurable max retries for cross-provider fallback",
			tier1BoundaryHelp: "Minutes ago where the highest weight tier ends",
			tier1BoundaryLabel: "Tier 1 Boundary (minutes)",
			tier1WeightHelp: "Weight applied to the most-recent tier",
			tier1WeightLabel: "Tier 1 Weight",
			tier2BoundaryHelp: "Minutes ago where the medium weight tier ends",
			tier2BoundaryLabel: "Tier 2 Boundary (minutes)",
			tier2WeightHelp: "Weight applied to the medium-recency tier",
			tier2WeightLabel: "Tier 2 Weight",
			tier3WeightHelp: "Weight applied to the oldest tier within the window",
			tier3WeightLabel: "Tier 3 Weight",
			strategy: {
				autoDescription:
					"Balance price, reliability, speed, and cache support.",
				autoLabel: "Automatic (recommended)",
				latencyDescription:
					"Strongly prefer the lowest time-to-first-token (streaming requests).",
				latencyLabel: "Lowest latency",
				priceDescription: "Strongly prefer the lowest-cost provider.",
				priceLabel: "Cheapest",
				throughputDescription:
					"Strongly prefer the fastest-generating provider.",
				throughputLabel: "Highest throughput",
			},
		},
		securityEvents: {
			accessDenied: "Access Denied",
			accessDeniedDescription:
				"Only organization owners and admins can view security events.",
			action: "Action",
			actionFilter: "Action:",
			allActions: "All actions",
			allCategories: "All categories",
			blocked: "Blocked",
			category: "Category",
			categoryFilter: "Category:",
			description: "View all guardrail violations and security events",
			details: "Details",
			failedLoad: "Failed to load security events",
			last24Hours: "Last 24 Hours",
			lastDays: "Last {days} days",
			loadMore: "Load More",
			loading: "Loading...",
			noEvents: "No security events found",
			overview: "Overview",
			pattern: "Pattern: {pattern}",
			redacted: "Redacted",
			rule: "Rule",
			title: "Security Events",
			totalViolations: "Total Violations",
			warned: "Warned",
		},
		settings: {
			account: "Account",
			billingEmail: "Billing Email",
			billingEmailDescription:
				"Manage your organization's billing email address.",
			billingEmailRequired: "Billing email is required",
			billingInformation: "Billing Information",
			billingInformationDescription:
				"Configure billing details for invoices and receipts",
			billingAddress: "Billing Address (Optional)",
			billingAddressDescription: "Full billing address to appear on invoices.",
			billingCompany: "Company Name (Optional)",
			billingCompanyDescription: "Company name to appear on invoices.",
			billingNotes: "Invoice Notes (Optional)",
			billingNotesDescription:
				"Optional notes to include at the bottom of invoices.",
			billingNotesPlaceholder:
				"Additional notes to appear at the bottom of invoices (e.g., VAT number, purchase order number)",
			billingTaxId: "Tax ID / VAT Number (Optional)",
			billingTaxIdDescription:
				"Tax identification number to appear on invoices.",
			caching: "Caching",
			cachingDescription: "Configure caching settings for your API requests",
			cachingFormDescription: "Configure caching for identical LLM requests",
			cacheDuration: "Cache Duration (seconds)",
			cacheDurationMax:
				"Cache duration must not exceed 31,536,000 seconds (1 year)",
			cacheDurationMin: "Cache duration must be at least 10 seconds",
			cacheDurationNote:
				"Note: changing this setting may take up to 5 minutes to take effect.",
			archiveProject: "Archive Project",
			archiveProjectDescription:
				"Permanently archive this project and all its data",
			archiveProjectConfirm:
				'This will archive "{projectName}" and remove it from your project selector. All API keys for this project will be disabled. While historical data will be preserved, the project will no longer be accessible.',
			archiving: "Archiving...",
			archived: "Project archived",
			archivedDescription: "The project has been successfully archived.",
			archiveWill: "Archiving this project will:",
			archiveWarning: "This action cannot be undone",
			areYouSure: "Are you absolutely sure?",
			copyProjectId: "Copy project ID",
			copyOrganizationId: "Copy organization ID",
			dangerZone: "Danger Zone",
			dangerZoneDescription: "Irreversible and destructive actions",
			dataRetention: "Data Retention",
			dataRetentionDescription:
				"Configure how request payloads and AI responses are stored",
			dataRetentionPeriod:
				"Retained data is automatically cleaned up after 30 days. Contact us for enterprise plans with unlimited retention.",
			dataRetentionPeriodLabel: "Data Retention Period:",
			dataStorageBilling: "Data storage is billed at $0.01 per 1M tokens",
			dataStorageBillingDescription:
				"(includes input, cached, output, and reasoning tokens).",
			emailAddress: "Email Address",
			enableRequestCaching: "Enable request caching",
			failedBillingSave: "Failed to save billing settings.",
			failedCachingSave: "Failed to save caching settings.",
			failedOrganizationNameSave: "Failed to save organization name settings.",
			failedProjectModeSave: "Failed to save project mode settings.",
			failedProjectNameSave: "Failed to save project name.",
			failedRetentionSave: "Failed to save data retention settings.",
			failedRoutingStrategySave: "Failed to save routing strategy.",
			invalidEmail: "Please enter a valid email address",
			organizationId: "Organization ID",
			organizationIdDescription:
				"Use this ID when referencing your organization in the API or with support.",
			organizationName: "Organization Name",
			organizationNameDescription: "Manage your organization's name.",
			organizationNameRequired: "Organization name is required",
			organizationNameTooLong:
				"Organization name must be less than 255 characters",
			organizationLine: "Organization: {name}",
			metadataOnly: "Metadata Only",
			metadataOnlyDescription:
				"Save only metadata, pricing, and usage data (exclude request payloads and responses)",
			minMaxCacheDuration: "Min: 10, Max: 31,536,000 (one year)",
			name: "Name",
			noOrganizationId: "Please select an organization to view its ID.",
			noOrganizationBilling:
				"Please select an organization to configure billing settings.",
			noOrganizationName:
				"Please select an organization to configure name settings.",
			noOrganizationRetention:
				"Please select an organization to configure data retention settings.",
			organizationNameUpdated: "Your organization name has been updated.",
			organizationNotFound: "Organization not found.",
			preserveHistoricalData:
				"Preserve all historical data and logs for compliance purposes",
			preferences: "Preferences",
			projectId: "Project ID",
			projectIdDescription:
				"Use this ID when referencing your project in the API or with support.",
			projectLine: "Project: {name}",
			projectMode: "Project Mode",
			projectModeApiKeysDescription:
				"Use your own provider API keys (OpenAI, Anthropic, etc.)",
			projectModeCredits: "Credits",
			projectModeCreditsDescription:
				"Use your organization credits and our internal API keys",
			projectModeDescription:
				"Configure how your organization handles projects",
			projectModeFormDescription:
				"Configure how your project consumes LLM services",
			projectModeHybrid: "Hybrid",
			projectModeHybridDescription:
				"Use your own API keys when available, fall back to credits when needed",
			projectModeUpdated: "Your project mode settings have been updated.",
			projectNameRequired: "Project name is required",
			projectNameTooLong: "Project name must be less than 255 characters",
			projectName: "Project Name",
			projectNameDescription: "Update your project's display name",
			projectNameUpdated: "Your project name has been updated.",
			providerCacheWrites: "Provider Cache Writes",
			providerCacheWritesDescription: "Anthropic and AWS Bedrock (Claude) only",
			providerCacheWritesHelp:
				"When disabled, the gateway strips all {marker} markers from outgoing requests - both the ones it adds automatically for long prompts and any markers your client sends (e.g. Claude Code, Cursor, Cline). Cache writes are billed at 1.25x (5m) or 2x (1h) the input price; reads are 0.1x. Disable this if you send long prompts sporadically with gaps longer than the 5-minute cache TTL - otherwise you pay the write premium without ever benefiting from a cache read. Note: changing this setting may take up to 5 minutes to take effect.",
			retainAllData: "Retain All Data",
			retainAllDataDescription:
				"Save request payloads and AI responses along with metadata",
			removeFromSelector: "Remove the project from your project selector",
			disableProjectApiKeys:
				"Disable all API keys associated with this project",
			routingStrategyHelp:
				"Choose how the gateway selects a provider when a model is served by more than one. Individual requests can override this with the {field} field.",
			learnMore: "Learn more",
			tip: "Tip:",
			topUpTip:
				"Storage costs are deducted from credits in real-time. We recommend enabling auto top-up in {link} to prevent request failures when credits run out.",
			topUpTipPrefix:
				"Storage costs are deducted from credits in real-time. We recommend enabling auto top-up in",
			topUpTipSuffix: "to prevent request failures when credits run out.",
			billingSettings: "billing settings",
			updatedBilling: "Your billing information has been updated.",
			updatedCaching: "Your caching settings have been updated.",
			unableCachingSettings:
				"Unable to load caching settings. Please try again later.",
			unableProjectMode:
				"Unable to load project mode settings. Please try again later.",
			unableProjectSettings:
				"Unable to load project settings. Please try again later.",
			updatedRetention: "Your data retention settings have been updated.",
			updatedRoutingStrategy: "Your default routing strategy has been updated.",
			displayNameHelp: "This name will be displayed throughout the platform.",
		},
		security: {
			addPasskey: "Add Passkey",
			changePassword: "Change Password",
			confirmNewPassword: "Confirm New Password",
			currentPassword: "Current Password",
			error: "Error",
			genericError: "An error occurred",
			managePasskeys: "Manage your passkeys for passwordless login",
			newPassword: "New Password",
			passwordMismatch: "New passwords do not match",
			passwordUpdated: "Your password has been updated.",
			passkeys: "Passkeys",
			security: "Security",
			success: "Success",
			updatePassword: "Update Password",
			updatePasswordDescription: "Update your password",
			updating: "Updating...",
		},
		sdk: {
			addOneOriginPerLine: "One browser origin per line.",
			allowedOrigins: "Allowed origins",
			copySecretKey: "Copy secret key",
			copiedDescription: "The platform secret key has been copied.",
			createLiveKey: "Create Live Key",
			createTestKey: "Create Test Key",
			creating: "Creating...",
			endUserSessions: "End-user Sessions",
			enableEndUserSessions: "Enable end-user sessions",
			enableEndUserSessionsDescription:
				"Allow this project to mint short-lived browser session tokens.",
			error: "Error",
			failedCopy: "Failed to copy platform secret key.",
			failedCreate: "Failed to create platform secret key.",
			failedLoadKeys: "Failed to load platform secret keys.",
			failedLoadProject: "Project settings could not be loaded.",
			failedRevoke: "Failed to revoke platform secret key.",
			failedSave: "Failed to save SDK settings.",
			invalidAllowedOrigin: "Invalid allowed origin",
			invalidAllowedOriginMessage: "Invalid allowed origin: {origin}",
			llmSdk: "LLM SDK",
			llmSdkDescription:
				"Configure end-user sessions and platform secret keys for this project.",
			loadingKeys: "Loading keys...",
			markupPercent: "Markup percent",
			newSecretKey: "New secret key",
			noPlatformKeys: "No platform secret keys yet.",
			originsMustUseHttp: "Origins must use http or https.",
			platformKeyCopied: "Secret key copied",
			platformSecretKeys: "Platform Secret Keys",
			platformSecretKeysDescription:
				"Server-side keys for minting end-user sessions.",
			projectLine: "Project: {name}",
			revoke: "Revoke",
			revokeKey: "Revoke Key",
			revokeTitle: "Revoke secret key?",
			revokeDescription:
				"Backends using this key will stop minting end-user sessions immediately.",
			revoked: "Secret key revoked",
			revokedDescription: "The platform secret key has been revoked.",
			saveSuccess: "SDK project settings have been updated.",
			sdk: "SDK",
			secretCreated: "Secret key created",
			secretCreatedDescription: "Copy the key now. It will not be shown again.",
			test: "test",
			testSecretDescription: "SDK test secret",
			testSecretCreated: "Test secret key created",
			testSecretCreatedDescription:
				"Top-ups with this key use the Stripe sandbox. Copy it now - it won't be shown again.",
			liveSecretDescription: "SDK platform secret",
		},
		passkeys: {
			added: "Passkey added",
			addedDescription: "You can now sign in using your passkey",
			addDescription: "Add a passkey to enable passwordless login",
			addError: "Error adding passkey",
			addFailed: "An unexpected error occurred",
			addedColumn: "Added",
			deleted: "Passkey deleted",
			deletedDescription: "Your passkey has been removed.",
			device: "Device",
			failedLoad: "Failed to load passkeys.",
			noPasskeys: "No passkeys found",
			pleaseTryAgain: "Please try again",
			unknownDevice: "Unknown device",
		},
		team: {
			accessUpTo:
				"Organizations can have up to 5 team members. Contact us at {email} to unlock more seats.",
			addMember: "Add Member",
			addMemberDescription:
				"Add a new member to your organization by entering their email address.",
			addTeamMember: "Add Team Member",
			adding: "Adding...",
			admin: "Admin",
			adminDescription:
				"Can manage team members, projects, and API keys, but cannot access billing settings or modify owners.",
			developer: "Developer",
			developerDescription:
				"Can view and use projects and API keys, but cannot modify team or organization settings.",
			email: "Email",
			emailRequired: "Please enter an email address",
			fullAccessDescription:
				"Full access to all features including team management, billing, and organization settings.",
			loading: "Loading...",
			memberAdded: "Team member added successfully",
			memberRemoved: "Team member removed successfully",
			name: "Name",
			owner: "Owner",
			remove: "Remove",
			removeConfirm: "Are you sure you want to remove {name} from the team?",
			restrictedAccess: "Restricted Access",
			restrictedAccessDescription:
				"If you want a user to just access the API but not the dashboard or settings, add an API key for them and set specific permissions there.",
			role: "Role",
			rolePermissions: "Role Permissions",
			rolePermissionsDescription: "Understanding what each role can do",
			roleUpdated: "Role updated successfully",
			selectRole: "Select a role",
			seatsUsed:
				"Manage your organization's team members and their roles ({count}/5 seats used)",
			team: "Team",
			teamMembers: "Team Members",
		},
		timeRange: {
			extendedAnalytics: "Extended analytics",
			extendedAnalyticsDescription: "Available on Enterprise or self-hosted",
		},
		usage: {
			allApiKeys: "All API Keys",
			cache: "Cache",
			cacheRate: "Cache Rate",
			cacheRateDescription: "API request cache rate over time",
			costBreakdown: "Cost Breakdown",
			costBreakdownDescription: "Estimated costs by provider and model",
			costs: "Costs",
			errorRate: "Error Rate",
			errorRateDescription: "API request error rate over time",
			errors: "Errors",
			groupBy: "Group by",
			models: "Models",
			requests: "Requests",
			requestVolume: "Request Volume",
			requestVolumeDescription: "Number of API requests over time",
			title: "Usage & Metrics",
			topUsedModels: "Top Used Models",
			usageByApiKey: "Usage by API key",
			usageByModel: "Usage by model",
			usageBreakdownByApiKey: "Breakdown by API key",
			usageBreakdownByModel: "Breakdown by model",
			usageBreakdownByModelDescription: "Usage breakdown by model",
		},
	},
	"zh-CN": {
		account: {
			accountDeleted: "账号已删除",
			accountDeletedDescription: "你的账号已成功删除。",
			accountInformation: "账号信息",
			areYouSure: "确定要继续吗？",
			cancel: "取消",
			deleteAccount: "删除账号",
			deleteAccountConfirmDescription:
				"这会永久删除你的账号和个人数据，包括登录凭据和个人 API 密钥。此操作无法撤销。你购买和消耗额度的账单记录会按税务和会计法律要求保留 10 年。",
			deleteAccountDescription: "永久删除你的账号和个人数据",
			deleteAccountWarning:
				"此操作不可逆。你的账号和个人数据，包括登录凭据和个人 API 密钥，都将被永久删除。",
			deleting: "正在删除...",
			email: "邮箱",
			emailCannotChange: "邮箱无法修改",
			emailPassword: "邮箱和密码",
			error: "错误",
			genericError: "发生错误",
			name: "名称",
			privacyPolicy: "隐私政策",
			passkey: "通行密钥",
			retentionNoticeBeforeLink:
				"你购买和消耗额度的账单记录会按税务和会计法律要求保留 10 年。详情请查看",
			retentionNoticeAfterLink: "。",
			retentionNotice:
				"你购买和消耗额度的账单记录会按税务和会计法律要求保留 10 年。详情请查看{link}。",
			saveChanges: "保存修改",
			saving: "正在保存...",
			signedInVia: "登录方式",
			success: "成功",
			title: "账号",
			updateDescription: "更新你的账号详情",
			updatedDescription: "账号信息已更新。",
		},
		billing: {
			autoTopUpEnabled: "自动充值已启用",
			autoTopUpEnabledDescription:
				"余额低于 ${threshold} 时，将自动充值 ${amount}。",
			autoTopUpNudgeDescription: "余额低于 ${threshold} 时自动充值 ${amount}",
			autoTopUpNudgeTitle: "额度永不耗尽",
			dismiss: "暂不启用",
			paymentCanceled: "付款已取消",
			paymentCanceledDescription: "你的付款已取消。",
			paymentSuccessful: "付款成功",
			paymentSuccessfulDescription: "付款已成功处理。",
		},
		dashboard: {
			errorLoadingProjects: "项目加载失败",
			errorLoadingProjectsDescription:
				"无法加载此组织的项目。请检查网络连接后重试。",
			noProjectsFound: "未找到项目",
			noProjectsFoundDescription:
				"此组织还没有任何项目。创建一个项目即可开始使用。",
		},
		apiKeys: {
			activate: "启用",
			addRuleDescription:
				"添加访问控制规则，按类型、供应商或价格限制模型访问。",
			allowIpRanges: "允许 IP 范围 (CIDR)",
			allowPricingConstraints: "允许价格限制",
			allowSpecificModels: "允许指定模型",
			allowSpecificProviders: "允许指定供应商",
			allModelsAccessible: "当前可访问所有模型。可在上方创建规则来限制访问。",
			allTimeUsageLimit: "总用量限制",
			any: "不限",
			backToApiKeys: "返回 API 密钥",
			createdOn: "创建于 {date}",
			createNewRule: "创建新规则",
			createRule: "创建规则",
			currentPeriodResetDescription:
				"当前周期用量会在配置的时间窗口结束后重置。",
			deactivate: "停用",
			deleteRule: "删除规则",
			denyIpRanges: "拒绝 IP 范围 (CIDR)",
			denyPricingConstraints: "拒绝价格限制",
			denySpecificModels: "拒绝指定模型",
			denySpecificProviders: "拒绝指定供应商",
			durationRange: "时长必须介于 1 和 {max} {unit} 之间。",
			editLimits: "编辑 API 密钥限制",
			editLimitsDescription: "更新此密钥的总量限制和周期用量窗口。",
			enterAllTimeLimit: "请输入总用量限制，或关闭此限制。",
			enterRecurringLimit: "请输入周期用量限制，或关闭此限制。",
			every: "每",
			expirationDescription:
				"密钥过期后会自动停用。之后可以设置新的过期时间重新启用。",
			expirationRange: "过期时间必须介于 1 和 {max} {unit} 之间。",
			existingRules: "已有规则",
			expiresIn: "过期于",
			expiresOn: "过期时间：{date}。",
			freeOnly: "仅免费",
			groupModelsPlaceholder: "选择模型...",
			groupModelsSelected: "已选 {count} 个模型",
			groupProvidersPlaceholder: "选择供应商...",
			groupProvidersSelected: "已选 {count} 个供应商",
			iamDescription: "为 {keyName} 配置访问控制规则",
			ipRangesHelp:
				"用逗号或空白分隔。支持 IPv4 和 IPv6。网关会读取 {header} 中第一个条目作为客户端 IP（由 GCP 负载均衡器设置）。",
			invalidCidr: "CIDR 无效",
			invalidCidrDescription: "不是有效 CIDR：{cidr}",
			lastUsed: "上次使用",
			limitUsageIncludes:
				"用量包含 doteb 额度用量，以及适用时你自己的供应商密钥用量。",
			loadingRules: "正在加载规则...",
			manageRulesDescription: "管理此 API 密钥的访问控制规则。",
			maxInputPrice: "最高输入价格（$/百万 tokens）",
			maxOutputPrice: "最高输出价格（$/百万 tokens）",
			models: "模型",
			noConstraints: "无限制",
			noModelsFound: "没有找到模型。",
			noProvidersFound: "没有找到供应商。",
			paidOnly: "仅付费",
			pricingType: "价格类型",
			recurringLimitNonNegative: "周期用量限制必须为非负数。",
			recurringUsageLimit: "周期用量限制",
			restrictions: "限制条件",
			ruleCreated: "IAM 规则已创建",
			ruleDeleted: "IAM 规则已删除",
			ruleType: "规则类型",
			saveChanges: "保存修改",
			searchModels: "搜索模型...",
			searchProviders: "搜索供应商...",
			selectPricingType: "选择价格类型",
			setAllTimeUsageLimit: "设置总用量限制",
			setExpirationTtl: "设置过期时间 (TTL)",
			setRecurringUsageLimit: "设置周期用量限制",
			stabilityBeta: "测试版",
			stabilityExperimental: "实验性",
			stabilityUnstable: "不稳定",
			typeLabel: "类型：{type}",
			unit: "单位",
			usageLimitNonNegative: "总用量限制必须为非负数。",
			windowEvery: "每 {window}",
			windowReset: "{date} 重置",
			yourApiKeys: "你的 API 密钥",
		},
		auditLogs: {
			accessDenied: "访问被拒绝",
			accessDeniedDescription: "只有组织所有者和管理员可以查看审计日志。",
			action: "操作：",
			allActions: "全部操作",
			allResources: "全部资源",
			auditLogs: "审计日志",
			copyResourceId: "复制资源 ID 到剪贴板",
			copyToClipboard: "复制到剪贴板",
			copied: "已复制",
			description: "查看组织内所有操作的完整历史记录。",
			details: "详情",
			failedLoad: "审计日志加载失败",
			loadMore: "加载更多",
			loading: "加载中...",
			noLogs: "未找到审计日志",
			resource: "资源：",
			resourceColumn: "资源",
			resourceId: "资源 ID",
			timestamp: "时间",
			user: "用户",
		},
		activity: {
			activityLogs: "调用记录",
			agent: "个代理",
			agents: "个代理",
			agentsSummary: "{count} {label}",
			allIntegrations: "全部集成",
			allModels: "全部模型",
			allProviders: "全部供应商",
			allUnifiedReasons: "全部统一原因",
			backToAgents: "返回代理列表",
			cost: "成本",
			cursor: "Cursor",
			customHeaderKeyPlaceholder: "自定义请求头键（例如 uid）",
			customHeaderValuePlaceholder: "自定义请求头值（例如 12345）",
			duration: "耗时",
			errorLoadingAgents: "代理数据加载失败，请重试。",
			errorLoadingLogs: "日志加载失败",
			errorLoadingSessions: "会话加载失败，请重试。",
			filterByModel: "按模型筛选",
			filterByProvider: "按供应商筛选",
			filterBySource: "按来源筛选",
			filterByUnifiedReason: "按统一原因筛选",
			firstCallWorked: "第一次 API 调用成功了！",
			firstCallWorkedDescription:
				"充值额度即可解锁质量更高、速度更快、上下文更长的付费模型。",
			finishReasons: {
				canceled: "已取消",
				completed: "已完成",
				content_filter: "内容过滤",
				gateway_error: "网关错误",
				length_limit: "长度限制",
				tool_calls: "工具调用",
				unknown: "未知",
				upstream_error: "上游错误",
			},
			daysAgo: "{count} 天前",
			hoursAgo: "{count} 小时前",
			lastActive: "最近活跃",
			loadMore: "加载更多",
			loading: "加载中...",
			loadingAgents: "正在加载代理...",
			loadingMore: "正在加载更多...",
			loadingSessions: "正在加载会话...",
			loadMoreSessions: "加载更多会话",
			noAgentActivity: "还没有代理活动",
			noAgentActivityDescription:
				"当 Claude Code、Autohand Code、OpenCode、Cursor 或 Cline 等编码代理通过网关发起 API 请求后，这里会显示活动。",
			noLogs: "未找到匹配所选筛选条件的日志。",
			noModels: "未找到模型。",
			noProject: "请选择项目以查看最近日志。",
			noSessions: "还没有会话",
			noSessionsDescription:
				"当 Claude Code、Autohand Code、OpenCode、SoulForge 或 Cursor 等编码工具通过网关发起 API 请求后，会话会显示在这里。",
			noSessionsForAgent: "未找到此代理的会话。",
			project: "项目：{projectId}",
			recentEventsDescription: "最近的 API 请求和系统事件",
			refresh: "刷新",
			refreshing: "刷新中...",
			recently: "刚刚",
			request: "个请求",
			requests: "个请求",
			requestsTitle: "请求",
			searchModels: "搜索模型...",
			sessionIdPlaceholder: "会话 ID",
			topUpCredits: "充值额度",
			totalTokens: "总 tokens",
			tokens: "tokens",
			tokensTitle: "Tokens",
		},
		common: {
			actions: "操作",
			cancel: "取消",
			category: "分类",
			created: "创建时间",
			delete: "删除",
			disabled: "已停用",
			done: "完成",
			enabled: "已启用",
			error: "错误",
			loading: "加载中...",
			reset: "重置",
			save: "保存",
			saveChanges: "保存修改",
			saveSettings: "保存设置",
			saving: "正在保存...",
			settingsSaved: "设置已保存",
			success: "成功",
			unknown: "未知",
		},
		dateRange: {
			last1Hour: "最近 1 小时",
			last1Minute: "最近 1 分钟",
			last2Hours: "最近 2 小时",
			last3Days: "最近 3 天",
			last4Hours: "最近 4 小时",
			last5Minutes: "最近 5 分钟",
			last7Days: "最近 7 天",
			last12Hours: "最近 12 小时",
			last14Days: "最近 14 天",
			last24Hours: "最近 24 小时",
			last30Days: "最近 30 天",
			last30Minutes: "最近 30 分钟",
			searchPlaceholder: "搜索时间范围",
			selectTimeRange: "选择时间范围",
			today: "今天",
		},
		guardrails: {
			accessDenied: "访问被拒绝",
			accessDeniedDescription: "只有组织所有者和管理员可以管理护栏。",
			action: "操作",
			actionLabel: "操作：{action}",
			addFileTypePlaceholder: "添加文件类型（例如 pdf）",
			addRule: "添加规则",
			allow: "允许",
			allowLogOnly: "允许（仅记录）",
			allowedFileTypes: "允许的文件类型",
			block: "拦截",
			blockedTerms: "屏蔽词",
			blockedTermsLabel: "屏蔽词（每行一个）",
			configSaved: "配置已保存",
			customRegex: "自定义正则",
			customRules: "自定义规则",
			customRulesDescription: "创建自定义内容过滤规则",
			deleteRuleFailed: "删除规则失败",
			disabled: "已停用",
			documentLeakageDescription: "防止泄露机密文档内容",
			documentLeakageName: "文档泄露防护",
			enabled: "已启用",
			failedAddRule: "添加规则失败",
			failedLoad: "护栏配置加载失败",
			failedSave: "保存配置失败",
			failedUpdateRule: "更新规则失败",
			fileRestrictions: "文件限制",
			fileRestrictionsDescription: "配置允许的文件类型和大小限制",
			fileTypesDescription: "将上传限制为允许的文件类型",
			fileTypesName: "文件类型限制",
			guardrails: "护栏",
			guardrailsDescription: "为你的 LLM 应用配置内容安全规则",
			jailbreakDescription: "拦截绕过 AI 安全措施的尝试",
			jailbreakName: "越狱防护",
			maxFileSize: "最大文件大小（MB）",
			newRule: "新规则",
			noCustomRules: "尚未配置自定义规则。",
			noCustomRulesAdd: " 点击“添加规则”创建一个。",
			noCustomRulesEnable: " 启用护栏后可添加规则。",
			piiDescription: "检测并可选择打码个人身份信息",
			piiName: "PII 检测",
			promptInjectionDescription: "检测覆盖系统指令或注入恶意提示词的尝试",
			promptInjectionName: "提示词注入检测",
			redact: "打码",
			regexPattern: "正则表达式",
			restrictedTopics: "受限主题（每行一个）",
			ruleName: "规则名称",
			ruleNamePlaceholder: "例如：屏蔽竞品",
			ruleType: "规则类型",
			saveChanges: "保存修改",
			saving: "正在保存...",
			secretsDescription: "检测 API 密钥、密码和其他凭据",
			secretsName: "密钥检测",
			systemRules: "系统规则",
			systemRulesDescription: "由模式匹配驱动的内置安全规则",
			topicRestriction: "主题限制",
			warn: "警告",
		},
		logDetail: {
			additionalParameters: "附加参数",
			apiKey: "API 密钥",
			applied: "已应用",
			aspectRatio: "宽高比",
			audioInputCost: "音频输入成本",
			audioInputTokens: "音频输入 tokens",
			available: "可用",
			availableTools: "可用工具",
			backToActivity: "返回调用记录",
			billedToOrganization: "计入你的组织账单",
			builtinTools: "内置工具",
			cache: "缓存",
			cachedInputCost: "缓存输入成本",
			cachedInputTokens: "缓存输入 tokens",
			cacheWriteCost: "缓存写入成本",
			cacheWriteTokens: "缓存写入 tokens",
			canceled: "已取消",
			completionTokens: "补全 tokens",
			compression: "压缩",
			contentFilter: "内容过滤",
			contentFilterCost: "内容过滤成本",
			completed: "已完成",
			copyApiKeyId: "复制 API 密钥 ID",
			copyLogId: "复制日志 ID",
			copyProjectId: "复制项目 ID",
			copyRequestId: "复制请求 ID",
			copyTraceId: "复制追踪 ID",
			cost: "成本：${cost}",
			costInformation: "成本信息",
			customHeaders: "自定义请求头",
			dataStorage: "数据存储",
			date: "日期",
			discount: "折扣",
			discountOff: "{percent}% 优惠",
			duration: "耗时",
			error: "错误",
			errorDetails: "错误详情",
			errorMessage: "错误消息",
			effort: "强度",
			finishReason: "结束原因",
			frequencyPenalty: "频率惩罚",
			full: "完整",
			generatedImageAlt: "生成图片 {index}",
			imageCount: "图片数量",
			imageGeneration: "图片生成",
			imageInputCost: "图片输入成本",
			imageInputTokens: "图片输入 tokens",
			imageOutputCost: "图片输出成本",
			imageOutputTokens: "图片输出 tokens",
			imageQuality: "图片质量",
			imageSize: "图片尺寸",
			idPrefix: "ID：{id}",
			inferenceCost: "推理成本",
			inferenceTotal: "推理合计",
			inputCost: "输入成本",
			inputImageAlt: "输入图片 {index}",
			key: "密钥",
			logId: "日志 ID",
			logNotFound: "未找到日志",
			maxTokens: "最大 tokens",
			messageDataNotRetained:
				"消息数据未保留。可在组织策略中启用留存以保存请求消息。",
			messages: "消息",
			metadata: "元数据",
			modelMapping: "模型映射",
			mode: "模式",
			no: "否",
			noMessageData: "没有可用消息数据。",
			none: "无",
			noResponseContent: "没有可用响应内容。",
			notNeeded: "不需要",
			ok: "成功",
			outputCost: "输出成本",
			outputFormat: "输出格式",
			parameters: "参数",
			partial: "部分",
			plugins: "插件",
			pricingTier: "价格层级",
			project: "项目",
			promptTokens: "提示词 tokens",
			provider: "供应商",
			providerCost: "供应商成本",
			providerCostNoBalance: "供应商成本 - 不从你的余额扣除",
			providerPricing: "供应商定价",
			providerScores: "供应商评分",
			rpmCapped: "RPM 受限",
			reasoningContent: "推理内容",
			reasoningEffort: "推理强度",
			reasoningTokens: "推理 tokens",
			request: "请求",
			requestAttempts: "请求尝试",
			requestCost: "请求成本",
			requestedModel: "请求模型",
			requestedProvider: "请求供应商",
			requestId: "请求 ID",
			response: "响应",
			responseContentNotRetained:
				"响应内容未保留。可在组织策略中启用留存以保存响应数据。",
			responseFormat: "响应格式",
			responseHealing: "响应修复：",
			responseSize: "响应大小",
			retried: "已重试",
			retrySucceeded: "此请求已重试并成功。",
			routing: "路由",
			selection: "选择",
			serviceTier: "服务层级",
			seed: "Seed",
			source: "来源",
			statusCode: "状态码",
			statusText: "状态文本",
			streamed: "流式",
			throughput: "吞吐",
			tokens: "Tokens",
			temperature: "Temperature",
			toolCalls: "工具调用",
			toolChoice: "工具选择",
			tools: "工具",
			totalTokens: "总 tokens",
			topP: "Top P",
			traceId: "追踪 ID",
			ttft: "TTFT",
			unifiedFinishReason: "统一结束原因",
			unknown: "未知",
			up: "可用",
			usedMode: "实际模式",
			usedModel: "实际模型",
			videoOutputCost: "视频输出成本",
			viewSuccessfulRequest: "查看成功请求",
			webSearchCost: "网页搜索成本",
			webSearchUsed: "此请求使用了网页搜索",
			yes: "是",
		},
		masterKeys: {
			activateKey: "启用密钥",
			actions: "操作",
			createDescription:
				"主密钥允许你通过 /v1/master/* API 以编程方式创建项目和网关 API 密钥。",
			createMasterKey: "创建主密钥",
			created: "创建时间",
			createdBy: "创建者",
			deactivateKey: "停用密钥",
			deleteConfirmDescription:
				"此操作无法撤销。任何使用此主密钥的系统都会立即失去 /v1/master/* API 访问权限。",
			deleteConfirmShort: "此操作无法撤销。",
			deleteConfirmTitle: "删除这个主密钥？",
			deleted: "主密钥已删除。",
			description:
				"管理主密钥，以便通过 /v1/master/* API 以编程方式创建项目和网关 API 密钥。",
			done: "完成",
			failedCreate: "创建主密钥失败。",
			failedLoad: "主密钥加载失败，请重试。",
			keyCopied: "主密钥已复制",
			keyCopiedDescription: "主密钥已复制到剪贴板。",
			keyLimitReached: "已达到主密钥数量上限",
			lastUsed: "最近使用",
			limitMax: "每个组织最多 {max} 个主密钥",
			limitReachedContact: "已达到上限 - 请联系 contact@doteb.com 解锁更多",
			loading: "正在加载主密钥...",
			masterKey: "主密钥",
			masterKeysUsed: "主密钥：已使用 {current} / {max}",
			name: "名称",
			nameRequired: "请输入主密钥名称。",
			never: "从未",
			noKeys: "尚未创建主密钥。",
			openMenu: "打开菜单",
			placeholderName: "例如：租户开通",
			secureStore: "请安全保存此主密钥。之后将无法再次显示。",
			status: "状态",
			statusUpdated: "主密钥状态已更新。",
			title: "主密钥",
			viewDocs: "查看文档",
		},
		modeToggle: {
			dark: "深色",
			light: "浅色",
			system: "跟随系统",
			toggleTheme: "切换主题",
		},
		providerKeys: {
			addKeyDescription: "添加 {count} 个密钥",
			addKeysDescription: "添加 {count} 个密钥",
			apiKeysRegionSpecific: "API 密钥与区域相关。请确认你的密钥匹配所选区域。",
			apiVersion: "API 版本",
			azureApiVersionHint: "Azure API 版本（默认：2024-10-21 GA）",
			azureDeploymentHint:
				"选择 Azure AI Foundry（统一端点）或 Azure OpenAI（基于部署）",
			customNameHint: "用于模型名称，例如：{name}/gpt-4o",
			deploymentType: "部署类型",
			googleProjectHint:
				"你的 Google Cloud 项目 ID，可在 Google Cloud Console 中找到。非 lite Vertex AI 模型需要填写。",
			googleProjectId: "Google Cloud 项目 ID",
			lastUpdated: "上次更新",
			organizationLine: "组织：{name}",
			region: "区域",
			resourceName: "资源名称",
			resourceNameHint:
				"Azure base URL 中的资源名称：https://<resource-name>.openai.azure.com",
			selectDeploymentType: "选择部署类型",
			selectRegion: "选择区域",
			validationModel: "验证模型",
			validationModelHint:
				"用于验证 API 密钥的模型部署名称（默认：gpt-4o-mini）",
			yourProviderKeys: "你的供应商密钥",
		},
		routing: {
			availableAllPlans: "所有计划均可使用。",
			cacheHelp: "支持提示词缓存的供应商会获得加分",
			cacheLabel: "缓存",
			cachePromptTokensHelp: "纳入提示词缓存因素的最小提示词长度",
			cachePromptTokensLabel: "缓存提示词 tokens",
			contactSales: "联系销售",
			defaultLatencyHelp: "无指标时假定的延迟",
			defaultLatencyLabel: "默认延迟（ms）",
			defaultPlaceholder: "默认：{value}",
			defaultThroughputHelp: "无指标时假定的吞吐",
			defaultThroughputLabel: "默认吞吐（tok/s）",
			defaultUptimeHelp: "无指标时假定的可用率",
			defaultUptimeLabel: "默认可用率（%）",
			description: "选择此项目中网关如何选择供应商。",
			disabled: "已停用",
			enterprise: "企业版",
			enterpriseDescription: "路由覆盖能力仅企业版可用",
			enterpriseFeature: "企业版功能",
			enabledDescription: "关闭后，此项目会使用默认路由值。",
			explorationRateHelp: "路由到随机供应商的请求比例",
			explorationRateLabel: "探索比例",
			failedLoad: "路由配置加载失败",
			failedReset: "重置路由配置失败",
			failedSave: "保存路由配置失败",
			gatewayTimeoutHelp: "端到端请求最大耗时。必须 <= 基础设施默认值。",
			gatewayTimeoutLabel: "网关超时（ms）",
			historyDescription:
				"评分时考虑多少分钟的供应商指标，以及最近指标相对旧指标的权重。",
			historyTitle: "指标历史窗口",
			imagePriceHelp: "图片生成模型的价格权重",
			imagePriceLabel: "图片价格",
			latencyHelp: "流式响应时间权重",
			latencyLabel: "延迟",
			lowUptimeFallbackHelp: "如果请求的供应商低于此值，则自动改路由",
			lowUptimeFallbackLabel: "低可用率回退（%）",
			maxRetriesHelp: "跨供应商回退的最大尝试次数",
			maxRetriesLabel: "最大重试次数",
			mustBeAtLeast: "必须 >= {min}。",
			mustBeAtMost: "必须 <= {max}（基础设施上限）。",
			plainTimeoutHelp: "非流式请求的最大上游耗时。必须 <= 基础设施默认值。",
			plainTimeoutLabel: "普通请求超时（ms）",
			priceHelp: "基于成本排序的权重",
			priceLabel: "价格",
			providerPrioritiesDescription:
				"每个供应商的路由权重，范围 0 到 1。设为 0 会完全排除该供应商。",
			providerPrioritiesTitle: "供应商优先级",
			resetAll: "全部重置",
			resetDefaults: "路由配置已重置为默认值",
			retryDescription: "供应商失败或可用率较低时的行为。",
			retryTitle: "重试与回退",
			routingStrategy: "路由策略",
			routingStrategyDescription: "设置此项目默认的供应商选择策略。",
			saved: "路由配置已保存",
			scoreMarginHelp: "只有竞争供应商评分至少高出此值时，才从首选供应商切走。",
			scoreMarginLabel: "评分差值",
			scoringWeightsDescription: "权重越高，该因素对供应商选择影响越大。",
			scoringWeightsTitle: "评分权重",
			selectStrategy: "选择策略",
			sessionStickinessDescription:
				"将共享会话 id（{sessionHeader} 请求头，或 OpenAI {cacheKey}/{userField} 字段）的所有请求固定到同一个供应商，使多轮对话保持上游提示词缓存热度。关闭后，每个请求都会独立评分，不再考虑会话 id。",
			sessionStickinessTitle: "会话粘性",
			stickyDescription:
				"只要供应商保持健康且有竞争力，就持续将同一模型路由到同一供应商。减少不必要的切换，避免冷缓存和上游限流突增。",
			stickyTitle: "粘性路由",
			streamingTimeoutHelp: "流式请求的最大上游耗时。必须 <= 基础设施默认值。",
			streamingTimeoutLabel: "流式超时（ms）",
			thresholdsDescription: "评分算法使用的默认值和阈值。",
			thresholdsTitle: "阈值",
			throughputHelp: "每秒 tokens 效率权重",
			throughputLabel: "吞吐",
			timeoutsDescription:
				"请求和上游超时，单位毫秒。基础设施层会将显示的默认值作为硬上限，覆盖值只能缩短超时，不能延长。",
			timeoutsTitle: "超时",
			title: "路由",
			ttlSecondsHelp: "首选供应商在重新评估前保留多久。上限 24 小时。",
			ttlSecondsLabel: "TTL（秒）",
			tuneDescription: "为此项目调整供应商选择权重、阈值、重试和超时。",
			uptimeHelp: "供应商可用率权重",
			uptimeLabel: "可用率",
			uptimePenaltyHelp: "低于此可用率时应用指数惩罚",
			uptimePenaltyLabel: "可用率惩罚阈值（%）",
			uptimeThresholdHelp: "如果首选供应商可用率低于此值，则立即切走。",
			uptimeThresholdLabel: "可用率阈值（%）",
			windowMinutesHelp: "纳入多少分钟的供应商指标（最多 120）",
			windowMinutesLabel: "窗口（分钟）",
			whatsIncluded: "包含内容：",
			included1: "价格、可用率、吞吐、延迟和提示词缓存的自定义评分权重",
			included2: "按供应商设置路由优先级（或完全排除某个供应商）",
			included3: "自定义探索比例和低可用率回退阈值",
			included4: "项目级网关和上游请求超时",
			included5: "跨供应商回退的最大重试次数可配置",
			tier1BoundaryHelp: "最高权重层级结束于多少分钟前",
			tier1BoundaryLabel: "层级 1 边界（分钟）",
			tier1WeightHelp: "应用于最近层级的权重",
			tier1WeightLabel: "层级 1 权重",
			tier2BoundaryHelp: "中等权重层级结束于多少分钟前",
			tier2BoundaryLabel: "层级 2 边界（分钟）",
			tier2WeightHelp: "应用于中等新近度层级的权重",
			tier2WeightLabel: "层级 2 权重",
			tier3WeightHelp: "应用于窗口内最旧层级的权重",
			tier3WeightLabel: "层级 3 权重",
			strategy: {
				autoDescription: "平衡价格、可靠性、速度和缓存支持。",
				autoLabel: "自动（推荐）",
				latencyDescription:
					"强烈优先选择首 token 时间最低的供应商（流式请求）。",
				latencyLabel: "最低延迟",
				priceDescription: "强烈优先选择成本最低的供应商。",
				priceLabel: "最便宜",
				throughputDescription: "强烈优先选择生成速度最快的供应商。",
				throughputLabel: "最高吞吐",
			},
		},
		securityEvents: {
			accessDenied: "访问被拒绝",
			accessDeniedDescription: "只有组织所有者和管理员可以查看安全事件。",
			action: "操作",
			actionFilter: "操作：",
			allActions: "全部操作",
			allCategories: "全部分类",
			blocked: "已拦截",
			category: "分类",
			categoryFilter: "分类：",
			description: "查看所有护栏违规和安全事件",
			details: "详情",
			failedLoad: "安全事件加载失败",
			last24Hours: "最近 24 小时",
			lastDays: "最近 {days} 天",
			loadMore: "加载更多",
			loading: "加载中...",
			noEvents: "未找到安全事件",
			overview: "概览",
			pattern: "匹配模式：{pattern}",
			redacted: "已打码",
			rule: "规则",
			title: "安全事件",
			totalViolations: "违规总数",
			warned: "已警告",
		},
		settings: {
			account: "账号",
			billingEmail: "账单邮箱",
			billingEmailDescription: "管理组织的账单邮箱地址。",
			billingEmailRequired: "账单邮箱必填",
			billingInformation: "账单信息",
			billingInformationDescription: "配置发票和收据使用的账单详情",
			billingAddress: "账单地址（可选）",
			billingAddressDescription: "显示在发票上的完整账单地址。",
			billingCompany: "公司名称（可选）",
			billingCompanyDescription: "显示在发票上的公司名称。",
			billingNotes: "发票备注（可选）",
			billingNotesDescription: "显示在发票底部的可选备注。",
			billingNotesPlaceholder:
				"显示在发票底部的附加备注（例如 VAT 编号、采购订单号）",
			billingTaxId: "税号 / VAT 编号（可选）",
			billingTaxIdDescription: "显示在发票上的税务识别号。",
			caching: "缓存",
			cachingDescription: "配置 API 请求的缓存设置",
			cachingFormDescription: "为相同的 LLM 请求配置缓存",
			cacheDuration: "缓存时长（秒）",
			cacheDurationMax: "缓存时长不能超过 31,536,000 秒（1 年）",
			cacheDurationMin: "缓存时长至少为 10 秒",
			cacheDurationNote: "注意：修改此设置最多可能需要 5 分钟生效。",
			archiveProject: "归档项目",
			archiveProjectDescription: "永久归档此项目及其全部数据",
			archiveProjectConfirm:
				"这会归档“{projectName}”，并将它从项目选择器中移除。此项目的所有 API 密钥都会被停用。历史数据会保留，但该项目将无法再访问。",
			archiving: "归档中...",
			archived: "项目已归档",
			archivedDescription: "项目已成功归档。",
			archiveWill: "归档此项目将会：",
			archiveWarning: "此操作无法撤销",
			areYouSure: "确定要继续吗？",
			copyProjectId: "复制项目 ID",
			copyOrganizationId: "复制组织 ID",
			dangerZone: "危险区",
			dangerZoneDescription: "不可逆且具有破坏性的操作",
			dataRetention: "数据留存",
			dataRetentionDescription: "配置请求载荷和 AI 响应的存储方式",
			dataRetentionPeriod:
				"保留数据会在 30 天后自动清理。如需无限留存，请联系我们了解企业版计划。",
			dataRetentionPeriodLabel: "数据留存周期：",
			dataStorageBilling: "数据存储按 $0.01 / 百万 tokens 计费",
			dataStorageBillingDescription: "（包含输入、缓存、输出和推理 tokens）。",
			emailAddress: "邮箱地址",
			enableRequestCaching: "启用请求缓存",
			failedBillingSave: "账单设置保存失败。",
			failedCachingSave: "缓存设置保存失败。",
			failedOrganizationNameSave: "组织名称设置保存失败。",
			failedProjectModeSave: "项目模式设置保存失败。",
			failedProjectNameSave: "项目名称保存失败。",
			failedRetentionSave: "数据留存设置保存失败。",
			failedRoutingStrategySave: "路由策略保存失败。",
			invalidEmail: "请输入有效的邮箱地址",
			organizationId: "组织 ID",
			organizationIdDescription: "在 API 或支持沟通中引用组织时使用此 ID。",
			organizationName: "组织名称",
			organizationNameDescription: "管理组织名称。",
			organizationNameRequired: "组织名称必填",
			organizationNameTooLong: "组织名称必须少于 255 个字符",
			organizationLine: "组织：{name}",
			metadataOnly: "仅元数据",
			metadataOnlyDescription:
				"仅保存元数据、价格和用量数据（不保存请求载荷和响应）",
			minMaxCacheDuration: "最小：10，最大：31,536,000（1 年）",
			name: "名称",
			noOrganizationId: "请选择组织以查看其 ID。",
			noOrganizationBilling: "请选择组织以配置账单设置。",
			noOrganizationName: "请选择组织以配置名称设置。",
			noOrganizationRetention: "请选择组织以配置数据留存设置。",
			organizationNameUpdated: "组织名称已更新。",
			organizationNotFound: "未找到组织。",
			preserveHistoricalData: "保留所有历史数据和日志以满足合规要求",
			preferences: "偏好设置",
			projectId: "项目 ID",
			projectIdDescription: "在 API 或支持沟通中引用项目时使用此 ID。",
			projectLine: "项目：{name}",
			projectMode: "项目模式",
			projectModeApiKeysDescription:
				"使用你自己的供应商 API 密钥（OpenAI、Anthropic 等）",
			projectModeCredits: "额度",
			projectModeCreditsDescription: "使用组织额度和我们的内部 API 密钥",
			projectModeDescription: "配置组织如何处理项目",
			projectModeFormDescription: "配置此项目如何消耗 LLM 服务",
			projectModeHybrid: "混合",
			projectModeHybridDescription:
				"可用时使用你自己的 API 密钥，需要时回退到额度",
			projectModeUpdated: "项目模式设置已更新。",
			projectNameRequired: "项目名称必填",
			projectNameTooLong: "项目名称必须少于 255 个字符",
			projectName: "项目名称",
			projectNameDescription: "更新项目显示名称",
			projectNameUpdated: "项目名称已更新。",
			providerCacheWrites: "供应商缓存写入",
			providerCacheWritesDescription: "仅 Anthropic 和 AWS Bedrock（Claude）",
			providerCacheWritesHelp:
				"关闭后，网关会从出站请求中移除所有 {marker} 标记，包括网关为长提示词自动添加的标记，以及客户端发送的任何标记（例如 Claude Code、Cursor、Cline）。缓存写入按输入价格的 1.25x（5 分钟）或 2x（1 小时）计费；读取按 0.1x 计费。如果你只是偶尔发送长提示词，且间隔超过 5 分钟缓存 TTL，建议关闭，否则会支付写入溢价但无法从缓存读取中获益。注意：修改此设置最多可能需要 5 分钟生效。",
			retainAllData: "留存全部数据",
			retainAllDataDescription: "保存请求载荷、AI 响应和元数据",
			removeFromSelector: "从项目选择器中移除此项目",
			disableProjectApiKeys: "停用此项目关联的所有 API 密钥",
			routingStrategyHelp:
				"当一个模型由多个供应商提供时，选择网关如何选择供应商。单个请求可以用 {field} 字段覆盖此设置。",
			learnMore: "了解更多",
			tip: "提示：",
			topUpTip:
				"存储费用会实时从额度扣除。建议在{link}中启用自动充值，避免额度耗尽时请求失败。",
			topUpTipPrefix: "存储费用会实时从额度扣除。建议在",
			topUpTipSuffix: "中启用自动充值，避免额度耗尽时请求失败。",
			billingSettings: "账单设置",
			updatedBilling: "账单信息已更新。",
			updatedCaching: "缓存设置已更新。",
			unableCachingSettings: "无法加载缓存设置，请稍后重试。",
			unableProjectMode: "无法加载项目模式设置，请稍后重试。",
			unableProjectSettings: "无法加载项目设置，请稍后重试。",
			updatedRetention: "数据留存设置已更新。",
			updatedRoutingStrategy: "默认路由策略已更新。",
			displayNameHelp: "此名称会在整个平台中显示。",
		},
		security: {
			addPasskey: "添加通行密钥",
			changePassword: "修改密码",
			confirmNewPassword: "确认新密码",
			currentPassword: "当前密码",
			error: "错误",
			genericError: "发生错误",
			managePasskeys: "管理用于无密码登录的通行密钥",
			newPassword: "新密码",
			passwordMismatch: "两次输入的新密码不一致",
			passwordUpdated: "密码已更新。",
			passkeys: "通行密钥",
			security: "安全",
			success: "成功",
			updatePassword: "更新密码",
			updatePasswordDescription: "更新你的密码",
			updating: "更新中...",
		},
		sdk: {
			addOneOriginPerLine: "每行一个浏览器 origin。",
			allowedOrigins: "允许的 origin",
			copySecretKey: "复制密钥",
			copiedDescription: "平台密钥已复制。",
			createLiveKey: "创建正式密钥",
			createTestKey: "创建测试密钥",
			creating: "创建中...",
			endUserSessions: "终端用户会话",
			enableEndUserSessions: "启用终端用户会话",
			enableEndUserSessionsDescription: "允许此项目签发短期浏览器会话 token。",
			error: "错误",
			failedCopy: "复制平台密钥失败。",
			failedCreate: "创建平台密钥失败。",
			failedLoadKeys: "平台密钥加载失败。",
			failedLoadProject: "项目设置加载失败。",
			failedRevoke: "撤销平台密钥失败。",
			failedSave: "SDK 设置保存失败。",
			invalidAllowedOrigin: "允许的 origin 无效",
			invalidAllowedOriginMessage: "允许的 origin 无效：{origin}",
			llmSdk: "LLM SDK",
			llmSdkDescription: "为此项目配置终端用户会话和平台密钥。",
			loadingKeys: "正在加载密钥...",
			markupPercent: "加价百分比",
			newSecretKey: "新密钥",
			noPlatformKeys: "还没有平台密钥。",
			originsMustUseHttp: "Origin 必须使用 http 或 https。",
			platformKeyCopied: "密钥已复制",
			platformSecretKeys: "平台密钥",
			platformSecretKeysDescription: "用于签发终端用户会话的服务端密钥。",
			projectLine: "项目：{name}",
			revoke: "撤销",
			revokeKey: "撤销密钥",
			revokeTitle: "撤销这个密钥？",
			revokeDescription: "使用此密钥的后端将立即停止签发终端用户会话。",
			revoked: "密钥已撤销",
			revokedDescription: "平台密钥已撤销。",
			saveSuccess: "SDK 项目设置已更新。",
			sdk: "SDK",
			secretCreated: "密钥已创建",
			secretCreatedDescription: "请立即复制密钥。之后不会再次显示。",
			test: "测试",
			testSecretDescription: "SDK 测试密钥",
			testSecretCreated: "测试密钥已创建",
			testSecretCreatedDescription:
				"使用此密钥充值会走 Stripe 沙盒。请立即复制，它之后不会再次显示。",
			liveSecretDescription: "SDK 平台密钥",
		},
		passkeys: {
			added: "通行密钥已添加",
			addedDescription: "现在可以使用通行密钥登录。",
			addDescription: "添加通行密钥以启用无密码登录",
			addError: "添加通行密钥失败",
			addFailed: "发生意外错误",
			addedColumn: "添加时间",
			deleted: "通行密钥已删除",
			deletedDescription: "你的通行密钥已移除。",
			device: "设备",
			failedLoad: "通行密钥加载失败。",
			noPasskeys: "未找到通行密钥",
			pleaseTryAgain: "请重试",
			unknownDevice: "未知设备",
		},
		team: {
			accessUpTo:
				"每个组织最多可有 5 名团队成员。请联系 {email} 解锁更多席位。",
			addMember: "添加成员",
			addMemberDescription: "输入邮箱地址，将新成员添加到组织。",
			addTeamMember: "添加团队成员",
			adding: "正在添加...",
			admin: "管理员",
			adminDescription:
				"可以管理团队成员、项目和 API 密钥，但无法访问账单设置或修改所有者。",
			developer: "开发者",
			developerDescription:
				"可以查看和使用项目与 API 密钥，但不能修改团队或组织设置。",
			email: "邮箱",
			emailRequired: "请输入邮箱地址",
			fullAccessDescription:
				"拥有所有功能的完整访问权限，包括团队管理、账单和组织设置。",
			loading: "加载中...",
			memberAdded: "团队成员已添加",
			memberRemoved: "团队成员已移除",
			name: "姓名",
			owner: "所有者",
			remove: "移除",
			removeConfirm: "确定要将 {name} 从团队中移除吗？",
			restrictedAccess: "受限访问",
			restrictedAccessDescription:
				"如果只希望用户访问 API，而不是控制台或设置，可以为其添加 API 密钥，并在密钥上设置具体权限。",
			role: "角色",
			rolePermissions: "角色权限",
			rolePermissionsDescription: "了解每个角色可以执行的操作",
			roleUpdated: "角色已更新",
			selectRole: "选择角色",
			seatsUsed: "管理组织团队成员及角色（已使用 {count}/5 席位）",
			team: "团队",
			teamMembers: "团队成员",
		},
		timeRange: {
			extendedAnalytics: "扩展分析",
			extendedAnalyticsDescription: "企业版或自托管可用",
		},
		usage: {
			allApiKeys: "全部 API 密钥",
			cache: "缓存",
			cacheRate: "缓存命中率",
			cacheRateDescription: "API 请求缓存命中率随时间变化",
			costBreakdown: "成本拆分",
			costBreakdownDescription: "按供应商和模型估算成本",
			costs: "成本",
			errorRate: "错误率",
			errorRateDescription: "API 请求错误率随时间变化",
			errors: "错误",
			groupBy: "分组方式",
			models: "模型",
			requests: "请求",
			requestVolume: "请求量",
			requestVolumeDescription: "API 请求数量随时间变化",
			title: "用量与指标",
			topUsedModels: "最常用模型",
			usageByApiKey: "按 API 密钥查看用量",
			usageByModel: "按模型查看用量",
			usageBreakdownByApiKey: "按 API 密钥拆分",
			usageBreakdownByModel: "按模型拆分",
			usageBreakdownByModelDescription: "按模型拆分用量",
		},
	},
} satisfies UiMessageModule;
